import React from 'react';
import { Smile, Frown, Meh } from 'lucide-react';

const EmotionalTrendsChart = ({ trends, periodType }) => {
  if (!trends || trends.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        No emotional trends data available yet. Keep reflecting to see your emotional patterns over time!
      </div>
    );
  }

  // Calculate overall stats
  const totalPositive = trends.reduce((sum, t) => sum + (t.positive * t.total_reflections / 100), 0);
  const totalNeutral = trends.reduce((sum, t) => sum + (t.neutral * t.total_reflections / 100), 0);
  const totalNegative = trends.reduce((sum, t) => sum + (t.negative * t.total_reflections / 100), 0);
  const totalReflections = trends.reduce((sum, t) => sum + t.total_reflections, 0);
  
  const avgPositive = Math.round((totalPositive / totalReflections) * 100) || 0;
  const avgNeutral = Math.round((totalNeutral / totalReflections) * 100) || 0;
  const avgNegative = Math.round((totalNegative / totalReflections) * 100) || 0;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-brand-accent-soft p-4 rounded-xl border border-brand-border">
          <div className="text-sm text-brand-accent font-medium mb-1">Total Reflections</div>
          <div className="text-3xl font-bold text-brand-ink">{Math.round(totalReflections)}</div>
        </div>
        
        <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl border border-green-200">
          <div className="flex items-center justify-between mb-1">
            <div className="text-sm text-green-700 font-medium">Positive</div>
            <Smile className="w-5 h-5 text-green-600" />
          </div>
          <div className="text-3xl font-bold text-green-900">{avgPositive}%</div>
          <div className="text-sm text-green-700 mt-1">Average</div>
        </div>
        
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-xl border border-gray-200">
          <div className="flex items-center justify-between mb-1">
            <div className="text-sm text-gray-700 font-medium">Neutral</div>
            <Meh className="w-5 h-5 text-gray-600" />
          </div>
          <div className="text-3xl font-bold text-gray-900">{avgNeutral}%</div>
          <div className="text-sm text-gray-700 mt-1">Average</div>
        </div>
        
        <div className="bg-gradient-to-br from-red-50 to-red-100 p-4 rounded-xl border border-red-200">
          <div className="flex items-center justify-between mb-1">
            <div className="text-sm text-red-700 font-medium">Challenging</div>
            <Frown className="w-5 h-5 text-red-600" />
          </div>
          <div className="text-3xl font-bold text-red-900">{avgNegative}%</div>
          <div className="text-sm text-red-700 mt-1">Average</div>
        </div>
      </div>

      {/* Period Breakdown Timeline */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-4">
          {periodType === 'weekly' ? 'Weekly' : 'Monthly'} Emotional Breakdown
        </h4>
        <div className="space-y-2">
          {trends.slice(0, 12).map((period, idx) => {
            const posWidth = period.positive;
            const neuWidth = period.neutral;
            const negWidth = period.negative;
            
            return (
              <div key={idx} className="bg-white p-3 rounded-lg border border-gray-200">
                <div className="flex justify-between items-center mb-2">
                  <div className="text-sm font-medium text-gray-700">
                    {period.period}
                  </div>
                  <div className="text-xs text-gray-500">
                    {period.total_reflections} reflection{period.total_reflections !== 1 ? 's' : ''}
                  </div>
                </div>
                <div className="flex h-6 rounded-full overflow-hidden">
                  {period.positive > 0 && (
                    <div 
                      className="bg-green-500 flex items-center justify-center text-xs text-white font-medium"
                      style={{ width: `${posWidth}%` }}
                      title={`Positive: ${posWidth}%`}
                    >
                      {posWidth > 15 && `${posWidth}%`}
                    </div>
                  )}
                  {period.neutral > 0 && (
                    <div 
                      className="bg-gray-400 flex items-center justify-center text-xs text-white font-medium"
                      style={{ width: `${neuWidth}%` }}
                      title={`Neutral: ${neuWidth}%`}
                    >
                      {neuWidth > 15 && `${neuWidth}%`}
                    </div>
                  )}
                  {period.negative > 0 && (
                    <div 
                      className="bg-red-500 flex items-center justify-center text-xs text-white font-medium"
                      style={{ width: `${negWidth}%` }}
                      title={`Challenging: ${negWidth}%`}
                    >
                      {negWidth > 15 && `${negWidth}%`}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {trends.length > 12 && (
          <p className="text-xs text-gray-500 mt-3 text-center">
            Showing last 12 periods • Total of {trends.length} periods with reflections
          </p>
        )}
      </div>
    </div>
  );
};

export default EmotionalTrendsChart;
